import React from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query";
import {
  Navigate,
  generatePath,
  useParams,
  useSearchParams,
} from "react-router";
import { isErrorMessage } from "@sscs/types";
import { LoadingDots } from "@sscs/ui";
import {
  FILESTREAM_PURL,
  FULL_REPORT_PATH,
  PAGE_NOT_FOUND_ROUTE,
} from "Constants";
import { useGetPurlFromVersionQuery } from "Services";
import { generateReportSearchParams } from "Utils";

type ErrorProps = {
  isError: boolean;
  error?: FetchBaseQueryError | SerializedError | undefined;
};

const Error = ({ isError, error }: ErrorProps): React.JSX.Element => {
  if (isError && error) {
    if ("status" in error && error.status === 404) {
      return <Navigate to={`/${PAGE_NOT_FOUND_ROUTE}`} replace />;
    } else if ("data" in error && isErrorMessage(error.data)) {
      return <div>Could not fetch report: {error.data.data.error}</div>;
    } else {
      return <div>Unknown error</div>;
    }
  } else {
    return <div>Unknown error</div>;
  }
};

const RedirectToNewReportURL = (): React.JSX.Element => {
  const { groupId, reportPage } = useParams();
  const [searchParams] = useSearchParams();

  const fileId = searchParams.get("file_id");
  const fileType = searchParams.get("file_type");
  const isRepro = searchParams.get("is_repro");

  const activePage = reportPage ? reportPage : "";

  const {
    data: version,
    isLoading: isVersionLoading,
    isFetching: isVersionFetching,
    isError: isVersionError,
    error: versionError,
  } = useGetPurlFromVersionQuery(
    fileType === "VERSION" && fileId ? { versionId: fileId } : skipToken,
  );

  if (isVersionLoading || isVersionFetching) {
    return <LoadingDots />;
  }

  if (isVersionError) {
    return <Error isError={isVersionError} error={versionError} />;
  }

  // There is no way to redirect to correct report if fileId param is not defined
  // or if groupId is missing
  if (!fileId || !groupId) {
    return <Navigate to={`/${PAGE_NOT_FOUND_ROUTE}`} />;
  }

  if (fileType === "FILESTREAM") {
    const searchParams = generateReportSearchParams({ isRepro: false });

    const newReportLink = generatePath(`${FULL_REPORT_PATH}/*`, {
      groupId: groupId,
      projectName: FILESTREAM_PURL.projectName,
      packageName: FILESTREAM_PURL.packageName,
      version: fileId,
      "*": activePage,
    });

    return (
      <Navigate
        to={{ pathname: `/${newReportLink}`, search: searchParams }}
        replace
      />
    );
  } else if (fileType === "VERSION" && version) {
    const searchParams = generateReportSearchParams({
      isRepro: isRepro === "true",
    });

    const newReportLink = generatePath(`${FULL_REPORT_PATH}/*`, {
      groupId: groupId,
      projectName: version.project_name,
      packageName: version.package_name,
      version: version.software_info.version,
      "*": activePage,
    });

    return (
      <Navigate
        to={{ pathname: `/${newReportLink}`, search: searchParams }}
        replace
      />
    );
  } else {
    return <Navigate to={`/${PAGE_NOT_FOUND_ROUTE}`} />;
  }
};

export default RedirectToNewReportURL;
